import React from "react";
import TopicDetailSwitch from "./topicDetailSwitch";
import { AudienceByPermission as AudiencesByPermission, TopicDetails, TopicToggleStatus } from "modules/topics/models";
import { GlobalApplicationState } from "globalApplicationState";
import { ConnectedProps, connect } from "react-redux";
import { BasicAudience } from "modules/audiences";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Disclaimer from "modules/common/components/disclaimer";
import { Grid } from "@mui/material";
import EmptyMandatoryAudienceChip from "./emptyMandatoryAudienceChip";

const text = {
    default: "Existing and new users in selected audiences will be subscribed to this topic when saved. If no audiences are selected, subscription will be applied to all users.",
    mandatory: "Users in selected audiences cannot unsubscribe from this topic. If no audiences are selected, subscription will be mandatory to all users.",
    restricted: "Content with restricted topics is only accessible to users who are part of the audiences you select. Audience restrictions must match the default and mandatory audiences (if any)."
};

const label = {
    default: "Set as default",
    mandatory: "Set as mandatory",
    restricted: "Set as restricted"
};

const errorMessage = "When restricting a topic to specific audiences, ensure that mandatory and default audiences only include the audiences selected for the restricted topic.";

const TopicDialogToggles: React.FunctionComponent<PropsWithRedux> = (props) => {
    const [defaultSelected, setDefaultSelected] = React.useState<BasicAudience[]>(props.prevAudiences.default);
    const [mandatorySelected, setMandatorySelected] = React.useState<BasicAudience[]>(props.prevAudiences.mandatory);
    const [restrictedSelected, setRestrictedSelected] = React.useState<BasicAudience[]>(props.prevAudiences.restricted);

    const [isRestrictionError, setIsRestrictionError] = React.useState(false);

    const [toggleStatus, setToggleStatus] = React.useState<TopicToggleStatus>(props.toggleStatus);

    const { isRestricted: isRestrictedOn } = toggleStatus;
    const { setIsError } = props;

    React.useEffect(() => {
        const missingAudienceError = restrictedSelected.length === 0 && isRestrictedOn;
        setIsError(isRestrictionError || missingAudienceError);
    }, [restrictedSelected, isRestrictedOn, isRestrictionError, setIsError]);

    React.useEffect(() => {
        const isDefaultValid = () : boolean =>
            (defaultSelected.length > 0 || !toggleStatus.isDefault)
            && defaultSelected.every(
                (sel: BasicAudience) => restrictedSelected.map(r => r.id).includes(sel.id)
            );

        const isMandatoryValid = () : boolean =>
            (mandatorySelected.length > 0 || !toggleStatus.isMandatory)
            && mandatorySelected.every(
                (sel: BasicAudience) => restrictedSelected.map(r => r.id).includes(sel.id)
            );

        if (!toggleStatus.isRestricted) {
            if (isRestrictionError) setIsRestrictionError(false);
            return;
        }

        if (isRestrictionError) {
            if (isDefaultValid() && isMandatoryValid()) setIsRestrictionError(false);
        }
        else {
            if (!isDefaultValid() || !isMandatoryValid()) setIsRestrictionError(true);
        }
    }, [defaultSelected, mandatorySelected, restrictedSelected, isRestrictionError, toggleStatus]);

    const onChangeDefault = (input: BasicAudience[]) => {
        setDefaultSelected(input);
        props.setInput({ defaultFor: input });
    };

    const onChangeMandatory = (input: BasicAudience[]) => {
        setMandatorySelected(input);
        props.setInput({ mandatoryFor: input });
    };

    const onChangeRestricted = (input: BasicAudience[]) => {
        setRestrictedSelected(input);
        props.setInput({ restrictedTo: input, restricted: true });
    };

    const onSwitchDefault = (isDefault: boolean) => {
        setToggleStatus({ ...toggleStatus, isDefault: isDefault });

        if (!isDefault) {
            onChangeDefault([]);
            props.setInput({ default: isDefault, defaultFor: [] });
        }
        else {
            props.setInput({ default: isDefault });
        }
    };

    const onSwitchMandatory = (isMandatory: boolean) => {
        setToggleStatus({ ...toggleStatus, isMandatory: isMandatory });

        if (!isMandatory) {
            onChangeMandatory([]);
            return props.setInput({ mandatory: isMandatory, mandatoryFor: [] });
        }
        else {
            props.setInput({ mandatory: isMandatory });
        }
    };

    const onSwitchRestricted = (isRestricted: boolean) => {
        setToggleStatus({ ...toggleStatus, isRestricted: isRestricted });

        if (!isRestricted) {
            onChangeRestricted([]);
            props.setInput({ restricted: isRestricted, restrictedTo: [] });
        }
        else {
            const defaultUMandatory = defaultSelected
                .concat(mandatorySelected
                    .filter((audience) => defaultSelected.map(d => d.id).indexOf(audience.id) < 0)
                );
            onChangeRestricted(defaultUMandatory);
        }
    };

    return (
        <React.Fragment>
            <TopicDetailSwitch
                label={label.default}
                hoverText={text.default}
                onSwitch={onSwitchDefault}
                setSelected={onChangeDefault}
                audiences={props.audiences}
                selected={defaultSelected}
                toggleStatus={toggleStatus.isDefault}
                defaultChipText={"All Users"}
            />

            <TopicDetailSwitch
                label={label.mandatory}
                hoverText={text.mandatory}
                onSwitch={onSwitchMandatory}
                setSelected={onChangeMandatory}
                audiences={props.audiences}
                selected={mandatorySelected}
                toggleStatus={toggleStatus.isMandatory}
                defaultChipText={"All Users"}
            />

            <TopicDetailSwitch
                label={label.restricted}
                hoverText={text.restricted}
                onSwitch={onSwitchRestricted}
                setSelected={onChangeRestricted}
                audiences={props.audiences}
                selected={restrictedSelected}
                toggleStatus={toggleStatus.isRestricted}
                defaultChipText={<EmptyMandatoryAudienceChip/>}
            />

            {isRestrictionError &&
                <Grid item >
                    <Disclaimer
                        icon={<InfoOutlinedIcon />}
                        text={errorMessage}
                        backgroundColor="#ffe9af" />
                </Grid>
            }
        </React.Fragment>
    );
};

interface ComponentProps {
    prevAudiences: AudiencesByPermission,
    toggleStatus: TopicToggleStatus,
    setIsError: (isError: boolean) => void,
    setInput: (input: Partial<TopicDetails>) => void;
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        audiences: state.audiences.audiences,
    })
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(TopicDialogToggles);